import { z } from 'zod';

export const LAUNCHER_POSITION_VALUES = [
  'bottom-right',
  'bottom-left',
  'top-right',
  'top-left',
] as const;

export type LauncherPosition = (typeof LAUNCHER_POSITION_VALUES)[number];

export const DEFAULT_LAUNCHER_POSITION: LauncherPosition = 'bottom-right';

export const launcherPositionSchema = z.enum(LAUNCHER_POSITION_VALUES);

export const LAUNCHER_POSITION_LABELS: Record<LauncherPosition, string> = {
  'bottom-right': 'Bottom right',
  'bottom-left': 'Bottom left',
  'top-right': 'Top right',
  'top-left': 'Top left',
};

/**
 * Top-level launcher placement (separate from the 4-corner value above).
 *  - `corner`   — floating FAB at one of the four fixed corners (default).
 *  - `advanced` — TSAI "Advanced" mode: the launcher button is anchored
 *    inside a customer-supplied page element (via `data-target`) and the
 *    chat panel slides over from a body-level overlay.
 * Inline-panel (#117, `data-target-id`) is intentionally NOT a branding
 * placement option — it is an advanced manual script tag documented on the
 * Deploy page.
 */
export const LAUNCHER_PLACEMENT_VALUES = ['corner', 'advanced'] as const;

export type LauncherPlacement = (typeof LAUNCHER_PLACEMENT_VALUES)[number];

export const DEFAULT_LAUNCHER_PLACEMENT: LauncherPlacement = 'corner';

export const launcherPlacementSchema = z.enum(LAUNCHER_PLACEMENT_VALUES);

export const LAUNCHER_PLACEMENT_LABELS: Record<LauncherPlacement, string> = {
  corner: 'Fixed corner',
  advanced: 'Advanced — anchor button to an element',
};

const LAUNCHER_POSITION_SET = new Set<string>(LAUNCHER_POSITION_VALUES);

/** Returns a valid corner or `undefined` when the value is missing or invalid. */
export function parseLauncherPosition(raw: unknown): LauncherPosition | undefined {
  if (typeof raw !== 'string') {
    return undefined;
  }
  const value = raw.trim() as LauncherPosition;
  return LAUNCHER_POSITION_SET.has(value) ? value : undefined;
}

/** Coerces unknown DB/API values to a valid launcher corner. */
export function normalizeLauncherPosition(raw: unknown): LauncherPosition {
  return parseLauncherPosition(raw) ?? DEFAULT_LAUNCHER_POSITION;
}
